import { SUPPLEMENT_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack } from "expo-router";
import { useMemo, useState } from "react";

import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { ShareButton } from "~/components/ShareButton";
import type { Supplement } from "~/entities/medical/supplement";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";

interface SupplementListingItem {
    id: string;
    title: string;
}

const type = MedicalType.supplement;

export default function SupplementsIndex() {
    const { listMedical, refreshMedicalCache } = useMedical();
    const { findOption } = useOptions();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);
        await refreshMedicalCache(type).finally(() => setRefreshing(false));
    };

    const items: SupplementListingItem[] = useMemo(() => {
        return listMedical(type).map((s: Supplement) => ({
            id: s.id as string,
            title: findOption(OptionType.supplements, s.option_id)?.title as string,
        }));
    }, [findOption, listMedical]);

    const has_items = items.length > 0;

    return (
        <Authorize ability="view" section="supplements">
            <Stack.Screen
                options={{
                    headerRight: () => <ShareButton title="Share supplements" initial={["supplements", "photos"]} />,
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Supplements"
                    icon="medication"
                    button={() => (
                        <Can ability="update" section="supplements">
                            <Button
                                title="Add supplement"
                                variant="primary"
                                size="lg"
                                href="/health-profile/supplements/create"
                            />
                        </Can>
                    )}
                >
                    Track vitamins and supplements to stay on top of your treatment plan.
                </SectionHeader>
                <Only when={items.length}>
                    <FilteredList<SupplementListingItem>
                        data={items}
                        searchWith={(item, term) => item.title.toLowerCase().includes(term.toLowerCase())}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.title}
                                hideChevron
                                href={{ pathname: `${SUPPLEMENT_BASE_URL}/[id]/show`, params: { id: item.id } }}
                                last
                            />
                        )}
                    />
                </Only>
            </Page>
        </Authorize>
    );
}
